import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './Auth.css';
import { API_URL } from './config/apiConfig';
import { useContainerScale } from './hooks/useViewportScale';

function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('form'); // form, success, error, expired
  const [message, setMessage] = useState('');

  // Viewport scaling for responsive design
  const { 
    scale: viewportScale, 
    offsetX: viewportOffsetX, 
    offsetY: viewportOffsetY,
    baseWidth: containerBaseWidth,
    baseHeight: containerBaseHeight,
    isMobile: isContainerMobile
  } = useContainerScale();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.password) {
      newErrors.password = t('resetPassword.errors.passwordRequired');
    } else if (formData.password.length < 8) {
      newErrors.password = t('resetPassword.errors.passwordLength');
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])/.test(formData.password)) {
      newErrors.password = t('resetPassword.errors.passwordComplexity');
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = t('resetPassword.errors.passwordMismatch');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const apiUrl = API_URL;
      const response = await fetch(`${apiUrl}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          password: formData.password
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setStatus('success');
        setMessage(data.message || t('resetPassword.successMessage'));
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/');
        }, 3000);
      } else {
        if (data.error && data.error.includes('expired')) {
          setStatus('expired');
          setMessage(data.error);
        } else {
          setStatus('error');
          setMessage(data.error || t('resetPassword.errorMessage'));
        }
      }
    } catch (err) {
      setStatus('error');
      setMessage(t('resetPassword.errorMessage'));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="auth-viewport-wrapper">
        <div className="auth-fullscreen-background" />
        <div 
          className="auth-viewport-container"
          style={{
            width: `${containerBaseWidth}px`,
            height: `${containerBaseHeight}px`,
            transformOrigin: '0 0',
            transform: `translate(${viewportOffsetX}px, ${viewportOffsetY}px) scale(${viewportScale})`,
          }}
        >
          <div className={`auth-container ${isContainerMobile ? 'auth-mobile' : ''}`}>
            <div className="auth-card">
              <div className="auth-game-title">{t('welcome.gameTitle')}</div>
              <div className="auth-error">
                <p>{t('resetPassword.invalidToken')}</p>
              </div>
              <div className="auth-links">
                <Link to="/request-password-reset" className="auth-link">{t('resetPassword.requestNewLink')}</Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-viewport-wrapper">
      <div className="auth-fullscreen-background" />
      <div 
        className="auth-viewport-container"
        style={{
          width: `${containerBaseWidth}px`,
          height: `${containerBaseHeight}px`,
          transformOrigin: '0 0',
          transform: `translate(${viewportOffsetX}px, ${viewportOffsetY}px) scale(${viewportScale})`,
        }}
      >
        <div className={`auth-container ${isContainerMobile ? 'auth-mobile' : ''}`}>
          <div className="auth-card">
            <div className="auth-game-title">{t('welcome.gameTitle')}</div>
            
            {status === 'form' && (
              <>
                <p className="auth-subtitle" style={{ marginTop: 20, marginBottom: 30 }}>
                  {t('resetPassword.description')}
                </p>
                
                <form className="auth-form" onSubmit={handleSubmit}>
                  <div className="auth-input-group">
                    <input
                      type="password"
                      name="password"
                      className="auth-input"
                      placeholder={t('resetPassword.newPasswordPlaceholder')}
                      value={formData.password}
                      onChange={handleChange}
                    />
                    {errors.password && <div className="field-error">{errors.password}</div>}
                  </div>

                  <div className="auth-input-group">
                    <input
                      type="password"
                      name="confirmPassword"
                      className="auth-input"
                      placeholder={t('resetPassword.confirmPasswordPlaceholder')}
                      value={formData.confirmPassword}
                      onChange={handleChange}
                    />
                    {errors.confirmPassword && <div className="field-error">{errors.confirmPassword}</div>}
                  </div>

                  <button 
                    type="submit" 
                    className="auth-button"
                    disabled={loading}
                  >
                    {loading ? t('resetPassword.resetting') : t('resetPassword.resetButton')}
                  </button>
                </form>

                <div className="auth-links">
                  <Link to="/" className="auth-link">{t('resetPassword.backToLogin')}</Link>
                </div>
              </>
            )}

            {status === 'success' && (
              <>
                <h1 className="auth-title" style={{ color: '#90ee90' }}>{t('resetPassword.successTitle')}</h1>
                <div className="auth-success">
                  <p>{message}</p>
                  <p>{t('resetPassword.redirecting')}</p>
                </div>
                <div className="auth-links">
                  <Link to="/" className="auth-link">{t('resetPassword.goToLogin')}</Link>
                </div>
              </>
            )}

            {status === 'expired' && (
              <>
                <h1 className="auth-title" style={{ color: '#ffa500' }}>{t('resetPassword.expiredTitle')}</h1>
                <div className="auth-error">
                  <p>{message}</p>
                </div>
                <div className="auth-links" style={{ flexDirection: 'row', justifyContent: 'center', gap: 15 }}>
                  <Link to="/request-password-reset" className="auth-link">
                    {t('resetPassword.requestNewLink')}
                  </Link>
                  <Link to="/" className="auth-link">
                    {t('resetPassword.backToLogin')}
                  </Link>
                </div>
              </>
            )}

            {status === 'error' && (
              <>
                <h1 className="auth-title" style={{ color: '#ff6b6b' }}>{t('resetPassword.failedTitle')}</h1>
                <div className="auth-error">
                  <p>{message}</p>
                </div>
                <div className="auth-links" style={{ flexDirection: 'row', justifyContent: 'center', gap: 15 }}>
                  <Link to="/request-password-reset" className="auth-link">
                    {t('resetPassword.requestNewLink')}
                  </Link>
                  <Link to="/" className="auth-link">
                    {t('resetPassword.backToLogin')}
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;
